import type { ReactNode } from 'react';
import type { HypothesisVerdict } from 'definitions';
import type { FoundationFieldName } from '@@/api/learnings/foundation/foundation';
import type { TestDebrief } from '@@/api/learnings/tests/test-debrief';
import type { LearningsScreenManager } from '@/state/learnings/useLearningsScreen';
import type { TestDebriefManager } from '@/state/learnings/useTestDebrief';
import { CircleAlert, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NavLink } from '@/components/NavLink';
import { cn } from 'helpers/cn';
import { OUTCOME_LABELS } from '@/state/experiments/library-learnings';
import { evidenceNotes } from '@/state/experiments/learnings-notes';
import { mechanismLabel } from '@/state/experiments/expected-mechanisms';
import {
  EXPECTATION_CHOICES,
  NEXT_STEP_CHOICES,
  OUTSIDE_FACTOR_CHOICES,
  labelOf,
} from '@/state/experiments/report-questions';
import { changeTypeLabel, pageLabel, signedPercent } from '@/state/learnings/page-view';
import { ActionButton, EmptyState, Fact, MICRO_LABEL_SHAPE, Panel } from '../../ConsolePanel';
import { Section, TagPill, ToneChip } from './parts';

const VERDICT_LABELS: Record<HypothesisVerdict, string> = {
  supported: 'Supports the hypothesis',
  contradicted: 'Contradicts the hypothesis',
  'says-nothing': 'Says nothing about the hypothesis',
};

const FOUNDATION_LABELS: Record<FoundationFieldName, string> = {
  hypothesis: 'Hypothesis',
  description: 'Description',
  mechanism: 'Expected mechanism',
  pxl: 'PXP score',
  debrief: 'Debrief',
};

const LEFT_OUT: Record<string, string> = {
  'tracking-or-release':
    'The team noted a tracking issue or a site release, so this result does not count toward any pooled estimate. The debrief below is still kept.',
  'broken-split':
    'The traffic split was off, so this result is left out of every pooled estimate. That is measured, not noted, so it cannot be included.',
  'no-data': 'This test has no result to pool, so it is left out of every pooled estimate.',
};

const dateRange = (start: number | null, end: number | null) => {
  const day = (stamp: number) =>
    new Date(stamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return start && end ? `${day(start)} to ${day(end)}` : end ? `ended ${day(end)}` : '';
};

type DebriefViewProps = { manager: TestDebriefManager; screen: LearningsScreenManager };

/** One concluded test: what it changed, what it taught, the team's read beside it, and where it counts. */
export const DebriefView = ({ manager, screen }: DebriefViewProps) => {
  const { debrief } = manager;
  if (manager.isLoading) return <EmptyState>Loading the debrief...</EmptyState>;
  if (!debrief) return <EmptyState>This test has no debrief to show.</EmptyState>;

  const pages = [...new Set(debrief.insight?.variants.map((variant) => variant.pageType) ?? [])];

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="num text-dim">#{debrief.experimentId}</span>
            <ToneChip tone={debrief.outcome === 'won' ? 'pos' : debrief.outcome === 'lost' ? 'neg' : 'dim'}>
              {OUTCOME_LABELS[debrief.outcome]}
              {debrief.lift !== null && ` · ${signedPercent(debrief.lift / 100)}`}
            </ToneChip>
            {debrief.devices.map((device) => (
              <TagPill key={device}>{device}</TagPill>
            ))}
            {pages.map((pageType) => (
              <TagPill key={pageType}>{pageLabel(pageType)}</TagPill>
            ))}
            <span className="num text-dim text-micro">{dateRange(debrief.startsAt, debrief.endsAt)}</span>
          </div>
          <h1 className="m-0 text-[18px] font-medium tracking-[-0.01em]">{debrief.name}</h1>
        </div>
        <Button asChild size="sm" variant="outline">
          <NavLink to={`/experiments/${debrief.experimentId}`}>
            Open experiment
            <ExternalLink className="size-[13px]" strokeWidth={1.5} aria-hidden="true" />
          </NavLink>
        </Button>
      </div>

      <InclusionNotice debrief={debrief} manager={manager} />

      <div className="flex flex-wrap items-start gap-[14px]">
        <div className="flex min-w-0 flex-[2.2_1_520px] flex-col gap-[14px]">
          <WhatChanged debrief={debrief} />
          <WhatItTaught debrief={debrief} manager={manager} onTestFurther={() => screen.openTab('opportunities')} />
          <ExpectedVsHappened debrief={debrief} />
        </div>
        <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-[14px]">
          <TeamRead debrief={debrief} />
          <Foundation debrief={debrief} />
          <Panel
            title="Would feed these patterns"
            description={
              debrief.inclusion.state === 'left-out'
                ? 'None while it is left out.'
                : 'The patterns this test counts in.'
            }
          >
            <div className="flex flex-wrap gap-1.5">
              {debrief.feeds.map((ref) => (
                <button
                  key={ref.key}
                  type="button"
                  onClick={() => screen.openPattern(ref.key)}
                  className={cn(
                    'border-input rounded-pill border border-dashed px-2.5 py-0.5 text-meta hover:bg-hover',
                    debrief.inclusion.state === 'left-out' ? 'text-dim' : 'text-foreground',
                  )}
                >
                  {ref.label}
                </button>
              ))}
              {debrief.feeds.length === 0 && <EmptyState>None yet.</EmptyState>}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

type DebriefProps = { debrief: TestDebrief };
type ManagedProps = DebriefProps & { manager: TestDebriefManager };

const InclusionNotice = ({ debrief, manager }: ManagedProps) => {
  const { inclusion } = debrief;
  if (inclusion.state === 'included-despite-factors') {
    return (
      <Notice tone="ok">
        <span className="flex-1">
          Included. The team said the noted problem did not spoil it, so it counts in the patterns.
        </span>
        {manager.canWrite && (
          <Button size="sm" variant="outline" disabled={manager.isIncluding} onClick={() => manager.setIncluded(false)}>
            Leave it out again
          </Button>
        )}
      </Notice>
    );
  }
  if (inclusion.state !== 'left-out' || !inclusion.reason) return null;
  return (
    <Notice tone="warn">
      <span className="flex-1 min-w-60 text-pretty">
        <strong className="font-medium">Left out of patterns.</strong> {LEFT_OUT[inclusion.reason]}
      </span>
      {inclusion.canInclude && manager.canWrite && (
        <Button size="sm" variant="outline" disabled={manager.isIncluding} onClick={() => manager.setIncluded(true)}>
          Tracking is fixed, include it
        </Button>
      )}
    </Notice>
  );
};

type NoticeProps = { tone: 'ok' | 'warn'; children: ReactNode };

const Notice = ({ tone, children }: NoticeProps) => (
  <div
    className={cn(
      'flex flex-wrap items-center gap-3 rounded-card border px-3 py-2.5 text-meta',
      tone === 'ok' ? 'border-ok-line bg-ok-bg' : 'border-warn-line bg-warn-bg',
    )}
  >
    <CircleAlert
      className={cn('size-[15px] shrink-0', tone === 'ok' ? 'text-ok' : 'text-warn')}
      strokeWidth={1.5}
      aria-hidden="true"
    />
    {children}
  </div>
);

const WhatChanged = ({ debrief }: DebriefProps) => {
  const labelled = debrief.insight?.variants ?? [];
  const variants = debrief.variants.filter((variant) => !variant.isControl);
  return (
    <Panel
      title="What changed"
      description={`${variants.length} ${variants.length === 1 ? 'variant' : 'variants'} against the control.`}
    >
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        {variants.map((variant, index) => {
          const label = labelled.find((entry) => entry.variantId === variant.variantId);
          return (
            <div key={variant.variantId} className="border-line overflow-hidden rounded-card border">
              <div className="bg-field border-line relative h-[150px] border-b">
                {variant.screenshots[0] ? (
                  <img
                    src={variant.screenshots[0]}
                    alt={`Screenshot of ${variant.name}`}
                    loading="lazy"
                    className="size-full object-cover object-top"
                  />
                ) : (
                  <span className="micro-label absolute bottom-2.5 left-2.5 normal-case tracking-[0.06em]">
                    No screenshot
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2 p-3">
                <span className="micro-label">Variant {index + 1}</span>
                <span className="text-body font-medium">{variant.name}</span>
                {label && <span className="text-meta text-pretty">{label.summary}</span>}
                {label?.details && <span className="text-dim text-meta text-pretty">{label.details}</span>}
                {label && (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {label.changeTypes.map((changeType) => (
                        <ToneChip key={changeType} tone="dim" className="text-[9px]">
                          {changeTypeLabel(changeType)}
                        </ToneChip>
                      ))}
                    </div>
                    <span className="supporting">
                      {pageLabel(label.pageType)} · {label.elementName}
                      {label.themeName && ` · Theme: ${label.themeName}`}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

type TaughtProps = ManagedProps & { onTestFurther: () => void };

const WhatItTaught = ({ debrief, manager, onTestFurther }: TaughtProps) => {
  const insight = debrief.insight;
  const reviewLabel = debrief.review
    ? `Written by AI · reviewed${debrief.review.by ? ` by ${debrief.review.by}` : ''}`
    : 'Written by AI · not reviewed';

  return (
    <Section
      tourAnchor="learnings-taught"
      title="What it taught"
      actions={
        insight?.learning ? (
          <div className="flex items-center gap-2.5">
            <span className="supporting">{reviewLabel}</span>
            {manager.canWrite && (
              <ActionButton onClick={() => manager.setReviewed(!debrief.review)} disabled={manager.isReviewing}>
                {debrief.review ? 'Unmark' : 'Mark reviewed'}
              </ActionButton>
            )}
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3 p-3.5">
        {insight?.learning ? (
          <>
            <p className="m-0 max-w-[70ch] text-[15px] font-medium leading-snug text-pretty">{insight.learning}</p>
            <div className="flex flex-wrap gap-1.5">
              {insight.verdict && <ToneChip tone="dim">{VERDICT_LABELS[insight.verdict]}</ToneChip>}
            </div>
            {evidenceNotes(insight.flags).map((note) => (
              <span key={note.key} className="text-warn text-meta">
                {note.text}
              </span>
            ))}
            {(insight.possibleReason || insight.nextTest) && (
              <div className="border-line grid gap-[18px] border-t pt-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
                {insight.possibleReason && (
                  <div className="flex flex-col gap-1.5">
                    <span className="micro-label">Possible reason</span>
                    <span className="text-meta text-pretty">{insight.possibleReason}</span>
                  </div>
                )}
                {insight.nextTest && (
                  <div className="flex flex-col items-start gap-1.5">
                    <span className="micro-label">Try next</span>
                    <span className="text-meta text-pretty">{insight.nextTest}</span>
                    <Button size="sm" variant="secondary" onClick={onTestFurther}>
                      Test this further
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <EmptyState>No learning has been written for this test yet.</EmptyState>
        )}
      </div>
    </Section>
  );
};

const ExpectedVsHappened = ({ debrief }: DebriefProps) => {
  const expected = [
    ...debrief.mechanisms.filter((id) => id !== 'custom').map(mechanismLabel),
    ...(debrief.customMechanism ? [debrief.customMechanism] : []),
  ];
  const read = debrief.teamRead?.expectation ? labelOf(EXPECTATION_CHOICES, debrief.teamRead.expectation) : 'Not given';

  return (
    <Section title="Expected vs what happened">
      <div className="flex flex-col gap-2.5 p-3.5">
        <div className="grid gap-[7px] [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
          <Fact label="Expected mechanism" value={expected.join(', ') || 'None'} />
          <Fact label="Team's read" value={read} />
          <Fact label="Result" value={OUTCOME_LABELS[debrief.outcome]} />
          {debrief.readCheck && (
            <div
              className={cn(
                'flex flex-col gap-1 rounded-card border px-3 py-2.5',
                debrief.readCheck === 'disagree' ? 'border-warn-line bg-warn-bg' : 'border-line',
              )}
            >
              <span className={cn(MICRO_LABEL_SHAPE, debrief.readCheck === 'disagree' ? 'text-warn' : 'text-dim')}>
                Check
              </span>
              <span className="text-body font-medium">
                {debrief.readCheck === 'disagree' ? 'Read and result disagree' : 'Read and result agree'}
              </span>
            </div>
          )}
        </div>
        {debrief.readCheck === 'disagree' && (
          <p className="text-dim m-0 max-w-[90ch] text-meta text-pretty">
            The team&apos;s read and the measured result say different things. Worth settling in the debrief before it
            feeds any pattern.
          </p>
        )}
      </div>
    </Section>
  );
};

const TeamRead = ({ debrief }: DebriefProps) => {
  const read = debrief.teamRead;
  return (
    <Panel
      title="The team's read"
      description={read?.author ? `From the debrief, by ${read.author}` : 'From the debrief'}
    >
      {read ? (
        <div className="flex flex-col gap-2.5">
          {(read.learning || read.explanation) && (
            <blockquote className="bg-panel2 border-line m-0 rounded-card border px-3 py-2.5 text-body text-pretty">
              “{read.learning ?? read.explanation}”
            </blockquote>
          )}
          <div className="flex flex-wrap gap-1.5">
            {read.expectation && (
              <ToneChip tone="dim">Expected: {labelOf(EXPECTATION_CHOICES, read.expectation)}</ToneChip>
            )}
            {read.nextStep && <ToneChip tone="dim">Next: {labelOf(NEXT_STEP_CHOICES, read.nextStep)}</ToneChip>}
            {read.outsideFactors.map((factor) => (
              <ToneChip key={factor} tone="warn">
                {labelOf(OUTSIDE_FACTOR_CHOICES, factor)}
              </ToneChip>
            ))}
          </div>
        </div>
      ) : (
        <EmptyState>The team has not written a debrief for this test.</EmptyState>
      )}
    </Panel>
  );
};

const Foundation = ({ debrief }: DebriefProps) => {
  const fields = Object.keys(FOUNDATION_LABELS) as FoundationFieldName[];
  const gaps = fields.filter((field) => debrief.foundation[field].status !== 'ok').length;
  return (
    <Section
      tourAnchor="learnings-test-foundation"
      title="Foundation"
      actions={
        gaps > 0 ? (
          <span className={cn(MICRO_LABEL_SHAPE, 'text-warn')}>
            {gaps} {gaps === 1 ? 'gap' : 'gaps'}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col px-3.5 pb-3.5 pt-1">
        {fields.map((field) => {
          const entry = debrief.foundation[field];
          const ok = entry.status === 'ok';
          return (
            <div key={field} className="border-line flex gap-2.5 border-b py-2">
              <span
                className={cn(
                  'mt-1 size-2 shrink-0 rounded-pill border-2',
                  ok ? 'border-ok bg-ok' : 'border-warn bg-transparent',
                )}
                aria-hidden="true"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-meta font-medium">{FOUNDATION_LABELS[field]}</span>
                <span className={cn('supporting text-meta', !ok && 'text-warn')}>{ok ? 'In place' : entry.note}</span>
              </div>
            </div>
          );
        })}
        {gaps > 0 && (
          <div className="pt-3">
            <Button asChild size="sm" variant="outline">
              <NavLink to={`/experiments/${debrief.experimentId}`}>Fill gaps in experiment</NavLink>
            </Button>
          </div>
        )}
      </div>
    </Section>
  );
};
